/* eslint-disable import/prefer-default-export */
import { rounding } from 'utils/widgetUtil'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import CountType, { CountTypeStrings } from 'enums/CountType'
import TableSortOption, { TableSortOptionStrings } from 'enums/TableSortOption'
import { ColSortingConfigs } from 'components/visualization/Table/types'

import { isRightToLeftText, RTL_MARK } from 'utils/rtlTextUtil'
import { MAX_INDEX } from '../constants'
import { IndexWidgetsType } from '../../types'

export const getIndexValue = (index: number) =>
  index < 0 || index >= MAX_INDEX ? MAX_INDEX : index

export const getDisplayIndex = (index: number) => {
  const indexValue = getIndexValue(index)
  const numberFormatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return indexValue === MAX_INDEX
    ? `${indexValue}+`
    : numberFormatter.format(rounding(indexValue, 2))
}

export const getPercentByIndex = (index: number, maxIndex: number) => {
  const indexValue = getIndexValue(index)
  const indexPercentage = (indexValue * 100) / maxIndex
  const displayIndex = getDisplayIndex(index)
  if (displayIndex === '0.00') return 0
  return indexPercentage
}

export const getSortingConfigByTableSortOption = (
  tableSortOption: TableSortOptionStrings,
  shouldFallbackSort: boolean
): ColSortingConfigs<'name' | 'value' | 'index'> => {
  switch (tableSortOption) {
    case TableSortOption.ALPHABET_ASC:
      return {
        field: 'name',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.ALPHABET_DSC:
      return {
        field: 'name',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.VALUE_ASC:
      return {
        field: 'value',
        order: TableSortOption.ASCEND
      }
    case TableSortOption.VALUE_DSC:
      return {
        field: 'value',
        order: TableSortOption.DESCEND
      }
    case TableSortOption.INDEX_ASC:
      return shouldFallbackSort
        ? {
            field: 'value',
            order: TableSortOption.ASCEND
          }
        : {
            field: 'index',
            order: TableSortOption.ASCEND
          }
    case TableSortOption.INDEX_DSC:
    default:
      return shouldFallbackSort
        ? {
            field: 'value',
            order: TableSortOption.DESCEND
          }
        : {
            field: 'index',
            order: TableSortOption.DESCEND
          }
  }
}

export const findMaxValue = (rowData: any) => {
  let maxValue = 0
  for (let i = 0; i < rowData.length; i += 1) {
    if (rowData[i]!.value! > maxValue) {
      maxValue = rowData[i].value!
    }
  }
  return maxValue
}

export const getDisplayText = (
  name: string,
  viewOption: InsightViewOptionStrings,
  emojiNameToDisplayMap: Record<string, string>
) => {
  switch (viewOption) {
    case InsightViewOption.EMOJIS_EMOJI:
    case InsightViewOption.EMOJIS_EMOTION:
    case InsightViewOption.EMOJIS_GROUP:
      return emojiNameToDisplayMap[name]
    default:
      if (isRightToLeftText(name) && name.charAt(0) === '#') {
        return RTL_MARK + name
      }
      return name
  }
}

export const getNumberFormatter = () =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3
  })

export const getBarColor = (countType: CountTypeStrings) =>
  countType === CountType.POSTS ? 'metric.mainMetrics.post' : 'metric.mainMetrics.authors'

export const getCompareIds = (widgetSetting: IndexWidgetsType) => {
  if (widgetSetting.comparedTopicInfos) {
    return !!widgetSetting.comparedTopicInfos.length
  }
  return false
}
